'use client';

import { fetchRealtyOfferTypes } from '@eliadmin/core/src/api/realty/realty-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useRealtyStore } from '../../../../stores/realty-store';
import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';
import {
  KOSICEREAL_REALTY_FILTER_RECIPE,
  type RealtyFilterTemplateSetting,
} from '../realty-filter-recipe';
import styles from '../realty-filter.module.css';
import { RealtyFilterCheckbox } from './utils/realty-filter-checkbox';

type RealtyFilterRealtyOfferTypeProps = {
  templateSetting?: RealtyFilterTemplateSetting;
};

export const RealtyFilterRealtyOfferType = ({
  templateSetting,
}: RealtyFilterRealtyOfferTypeProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const { setFilter, filter } = useRealtyStore();
  const instanceId = useId();
  const headingId = `${instanceId}-realty-filter-offer-heading`;
  const statusId = `${instanceId}-realty-filter-offer-status`;

  const {
    data: realtyOfferTypes,
    isError,
    isLoading,
  } = useQuery({
    queryKey: [CacheKey.REALTY_OFFER_TYPES, locale],
    queryFn: () => fetchRealtyOfferTypes({ locale }),
    enabled: templateSetting?.enabled !== false,
  });

  const isSelected = (id: number) => filter.offer_type?.includes(id) ?? false;

  const handleFilterUpdate = (realtyTypeId: number) => {
    if (!realtyOfferTypes?.data?.some(option => option.id === realtyTypeId)) return;

    if (isSelected(realtyTypeId)) {
      setFilter({
        offer_type: filter.offer_type?.filter(id => id !== realtyTypeId),
      });
    } else {
      setFilter({
        offer_type: [...(filter.offer_type || []), realtyTypeId],
      });
    }
  };

  if (templateSetting && !templateSetting.enabled) {
    return null;
  }

  const options = KOSICEREAL_REALTY_FILTER_RECIPE.offerTypeIds.map(id => {
    const backendOption = realtyOfferTypes?.data?.find(option => option.id === id);
    return {
      id,
      isAvailable: Boolean(backendOption),
      name: t(KOSICEREAL_REALTY_FILTER_RECIPE.offerTypeTranslationKeys[id]),
    };
  });
  return (
    <div className={styles.fieldGroup} style={{ order: templateSetting?.order }}>
      <section
        className={styles.section}
        aria-busy={isLoading || undefined}
        aria-describedby={isLoading || isError ? statusId : undefined}
        aria-labelledby={headingId}
        data-query-state={isLoading ? 'loading' : isError ? 'error' : 'ready'}
      >
        <h2 id={headingId} className={styles.heading}>
          {t('text1416')}
        </h2>
        {(isLoading || isError) && (
          <p id={statusId} className={styles.srOnly} role={isError ? 'alert' : 'status'}>
            {t(isError ? 'text530' : 'text439')}
          </p>
        )}
        <div className={styles.options}>
          {options.map(realtyType => (
            <RealtyFilterCheckbox
              key={realtyType.id}
              item={realtyType}
              isSelected={isSelected}
              handleFilterUpdate={handleFilterUpdate}
              disabled={!realtyType.isAvailable}
              filterKey="offer_type"
              idPrefix={`${instanceId}-realty-filter-offer`}
            />
          ))}
        </div>
      </section>
      <Separator className={styles.separator} />
    </div>
  );
};
